import CircularProgress from '@mui/material/CircularProgress';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import * as React from 'react';
import { AuthData, withAuthContext } from '../../context/auth';
import {
	EMPTY_GRACE_PERIOD_TABLE_LABEL,
	GRACE_PERIOD_INVOICE_LABEL,
	NOT_AVAILABLE_LABEL,
} from '../../data/labelsAndMessages';
import { GracePeriodResponse, Policy2 } from '../../types/policy';
import {
	getComparator,
	HeadCell,
	Order,
	stableSort,
} from '../../util/tableUtil';
import { StyledEmptyMessage } from '../common/index';
import SortableTable from '../SortableTable';
import GracePeriodUpdateModalTrigger from './GracePeriods/GracePeriodUpdateModalTrigger';

interface Props {
	gracePeriods: GracePeriodResponse[];
	policy: Policy2;
	authData: AuthData;
}

interface Data {
	id: string;
	startDate: string;
	settledDate: string;
	lapseDate: string;
	overrideCancellationDate: string;
	document: any;
	invoice: any;
	modalTrigger: any;
}

function createData(
	id: string,
	startDate: string,
	settledDate: string,
	lapseDate: string,
	overrideCancellationDate: string,
	document: any,
	invoice: any,
	modalTrigger: any,
): Data {
	return {
		id,
		startDate,
		settledDate,
		lapseDate,
		overrideCancellationDate,
		document,
		invoice,
		modalTrigger,
	};
}

const GracePeriodTable = (props: Props) => {
	const { gracePeriods, policy, authData } = props;
	const { hasPermission } = authData;
	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<string>('startDate');

	const emptyTable = !gracePeriods.length ? (
		<StyledEmptyMessage>{EMPTY_GRACE_PERIOD_TABLE_LABEL()}</StyledEmptyMessage>
	) : null;

	const headCells: HeadCell[] = [
		{ id: 'startDate', rightAlign: false, label: 'Start Date' },
		{ id: 'settledDate', rightAlign: false, label: 'Settled Date' },
		{ id: 'lapseDate', rightAlign: false, label: 'Lapse Date' },
		{
			id: 'cancellationOverrideDate',
			rightAlign: false,
			label: 'Override Cancellation Date',
		},
		{ id: 'document', rightAlign: false, label: 'Document' },
		{ id: 'invoice', rightAlign: false, label: 'Invoice' },
	];

	if (hasPermission('gracePeriod.update')) {
		headCells.push({ id: 'action', rightAlign: false, label: 'Actions' });
	}

	const rows: any = gracePeriods?.map(
		(gracePeriod: GracePeriodResponse, index: number) => {
			const hasDoc = !!gracePeriod.document;
			const documentDisplay = hasDoc ? (
				gracePeriod.document.url ? (
					<a key={`gp-doc-${index}`} href={gracePeriod.document.url}>
						{gracePeriod.document.displayName}
					</a>
				) : (
					<span
						key={`gp-doc-${index}`}
						style={{ display: 'flex', alignItems: 'center' }}
					>
						{gracePeriod.document.displayName}
						<CircularProgress size={15} style={{ marginLeft: '5px' }} />
					</span>
				)
			) : (
				NOT_AVAILABLE_LABEL()
			);
			const invoice = policy.invoices?.find(
				(i) => i.locator === gracePeriod.invoiceLocator,
			);
			const invoiceDisplay = invoice ? (
				<a
					href={`/policy/${gracePeriod.policyLocator}/invoices/${invoice.displayId}`}
				>
					{GRACE_PERIOD_INVOICE_LABEL()}
				</a>
			) : (
				NOT_AVAILABLE_LABEL()
			);
			return createData(
				gracePeriod.locator,
				authData.getFormattedDate(gracePeriod.startTimestamp),
				authData.getFormattedDate(gracePeriod.settledTimestamp),
				authData.getFormattedDate(gracePeriod.endTimestamp),
				authData.getFormattedDate(gracePeriod.cancelEffectiveTimestamp),
				documentDisplay,
				invoiceDisplay,
				<GracePeriodUpdateModalTrigger gracePeriod={gracePeriod} />,
			);
		},
	);

	const rowElements = stableSort(rows, getComparator(order, orderBy)).map(
		(row: any, index: number) => {
			return (
				<TableRow hover tabIndex={-1} key={`${row.locator}-${index}`}>
					<TableCell scope='row' padding='none'>
						{row.startDate}
					</TableCell>
					<TableCell>{row.settledDate}</TableCell>
					<TableCell>{row.lapseDate}</TableCell>
					<TableCell>{row.overrideCancellationDate}</TableCell>
					<TableCell>{row.document}</TableCell>
					<TableCell>{row.invoice}</TableCell>
					{authData.hasPermission('gracePeriod.update') && (
						<TableCell>{row.modalTrigger}</TableCell>
					)}
				</TableRow>
			);
		},
	);

	return (
		<SortableTable
			rows={rowElements}
			headCells={headCells}
			additionalMsg={emptyTable}
			order={order}
			setOrder={setOrder}
			orderBy={orderBy}
			setOrderBy={setOrderBy}
		/>
	);
};

export default withAuthContext<Props>(GracePeriodTable);
